import React, { useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Center,
  Flex,
  Icon,
  IconButton,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { AppIcons } from "constants/icons";
import { useConfigurationContext } from "context/ConfigurationContext";
import BenchmarkEditForm from "modules/benchmark/conatiners/BenchmarkEditForm";
import SplitView from "modules/common/SplitView";
import BenchmarkList from "modules/benchmark/conatiners/BenchmarkList";

// ---------------------------------------------------------------------------
// BenchmarkDetailPanel
// ---------------------------------------------------------------------------

const BenchmarkDetailPanel: React.FC<{ onReloadKnowledgeGraph: () => void }> = ({ onReloadKnowledgeGraph }) => {
  const { selectedBenchmarkId, setSelectedBenchmarkId, setSelectedBenchmark, setEditBenchmark, deleteBenchmarkById } =
    useConfigurationContext();
  const [triggerSubmit, setTriggerSubmit] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const closePanel = () => {
    setEditBenchmark(false);
    setSelectedBenchmark(null);
    setSelectedBenchmarkId(null);
  };

  if (!selectedBenchmarkId) {
    return (
      <Center h="full" flexDirection="column" gap={3} color="gray.400">
        <Icon as={AppIcons.benchmark} boxSize={10} />
        <Text fontSize="sm" mb={0}>
          Select a benchmark to edit its definition
        </Text>
      </Center>
    );
  }

  const handleConfirmDelete = async () => {
    setDeleteDialogOpen(false);
    await deleteBenchmarkById(selectedBenchmarkId.header?.id!);
  };

  return (
    <>
      <Flex direction="column" h="full">
        <Flex
          px={4}
          py={2.5}
          borderBottomWidth="1px"
          borderColor="gray.100"
          justify="space-between"
          align="center"
          flexShrink={0}
          bg="white"
          gap={3}
        >
          <Text
            fontWeight="700"
            color="gray.900"
            fontSize="sm"
            noOfLines={2}
            mb={0}
            lineHeight="1.35"
            textAlign="left"
            flex={1}
            minW={0}
          >
            {selectedBenchmarkId.header?.name}
          </Text>
          <Flex gap={1} align="center" flexShrink={0}>
            <Tooltip label="Delete benchmark" placement="top">
              <Button
                aria-label="Delete benchmark"
                leftIcon={<Icon as={AppIcons.delete} />}
                size="sm"
                variant="ghost"
                colorScheme="red"
                fontWeight="500"
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete
              </Button>
            </Tooltip>
            <Button
              size="sm"
              colorScheme="green"
              leftIcon={<Icon as={AppIcons.checkCircle} />}
              onClick={() => setTriggerSubmit(true)}
            >
              Save
            </Button>
            <Tooltip label="Close panel" placement="top">
              <IconButton
                aria-label="Close panel"
                icon={<Icon as={AppIcons.cancelX} boxSize={5} />}
                size="sm"
                variant="ghost"
                colorScheme="gray"
                onClick={closePanel}
              />
            </Tooltip>
          </Flex>
        </Flex>
        <Box flex={1} overflowY="auto">
          <BenchmarkEditForm
            benchmark={selectedBenchmarkId}
            onClose={closePanel}
            triggerSubmit={triggerSubmit}
            setTriggerSubmit={setTriggerSubmit}
            onReloadKnowledgeGraph={onReloadKnowledgeGraph}
          />
        </Box>
      </Flex>

      <AlertDialog
        isOpen={deleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeleteDialogOpen(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Benchmark
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete <strong>{selectedBenchmarkId.header?.name}</strong>? This action cannot be
              undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

// ---------------------------------------------------------------------------
// BenchmarksView
// ---------------------------------------------------------------------------

export interface BenchmarksViewProps {
  onReloadKnowledgeGraph: () => void;
}

const BenchmarksView: React.FC<BenchmarksViewProps> = ({ onReloadKnowledgeGraph }) => {
  const { editBenchmark, selectedBenchmarkId } = useConfigurationContext();
  const benchmarksDetailOpen = editBenchmark && selectedBenchmarkId !== null;

  return (
    <SplitView
      leftPanel={<BenchmarkList splitDetailOpen={benchmarksDetailOpen} />}
      rightPanel={<BenchmarkDetailPanel onReloadKnowledgeGraph={onReloadKnowledgeGraph} />}
      isRightPanelVisible={benchmarksDetailOpen}
      defaultRightWidth={35}
    />
  );
};

export default BenchmarksView;
